'use client';

import { Toolbar } from '@/components/Toolbar/Toolbar';
import { WorkbenchPage } from '@/components/WorkbenchPage/WorkbenchPage';
import { WorkbenchPageLoader } from '@/components/WorkbenchPage/WorkbenchPageLoader';
import { EditorType, type InitializeData } from '@/types/feedback';

import { ClientFeedbackMissingTarget } from './ClientFeedbackMissingTarget';
import { useTargetUrl } from './useTargetUrl';

const isDev = process.env.NODE_ENV !== 'production';

export interface ClientFeedbackClientProps {
  commentId?: string;
  /**
   * Opaque server-sealed request context for the comment write routes.
   * Required on the client workbench. Omitted on the partner workbench,
   * which does not create or update pins.
   */
  sealedContext?: string;
  initialTargetUrl?: string;
  metaSiteId: string;
  feedbackId: string;
  editorType?: EditorType;
  initialData?: InitializeData;
  allowAnonymousModal?: boolean;
  sidebarReadOnly?: boolean;
  isPartner?: boolean;
  /** The agency that owns the site, shown for replies by a Wix user. */
  agencyName?: string;
}

export function ClientFeedbackClient({
  commentId,
  sealedContext,
  initialTargetUrl,
  metaSiteId,
  feedbackId,
  editorType,
  initialData,
  allowAnonymousModal = false,
  sidebarReadOnly = false,
  isPartner = false,
  agencyName,
}: ClientFeedbackClientProps) {
  const targetUrl = useTargetUrl(initialTargetUrl);

  if (!targetUrl) {
    if (isDev) {
      return <ClientFeedbackMissingTarget />;
    }
    return (
      <div className="relative flex h-screen flex-col overflow-hidden">
        <Toolbar>{null}</Toolbar>
        <WorkbenchPageLoader />
      </div>
    );
  }

  return (
    <WorkbenchPage
      commentId={commentId}
      sealedContext={sealedContext}
      targetUrl={targetUrl}
      metaSiteId={metaSiteId}
      feedbackId={feedbackId}
      editorType={editorType}
      initialData={initialData}
      allowAnonymousModal={allowAnonymousModal}
      sidebarReadOnly={sidebarReadOnly}
      isPartner={isPartner}
      agencyName={agencyName}
    />
  );
}
